import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Edit2, Trash2, Save, X, Eye, CheckCircle, XCircle,
    ChevronRight, ChevronDown, Layers, GitBranch,
    ArrowRight, Clock, Briefcase, UserCheck,
    Activity, Hammer, GripVertical,
    ArrowUp, ArrowDown
} from "lucide-react";

import {
    getAllCareerPathsRequest,
    createCareerPathRequest,
    updateCareerPathRequest,
    deleteCareerPathRequest,
} from "../../../redux/actions/careerPathActions";

import {
    getStepsByCareerPathRequest,
    addStepRequest,
    updateStepRequest,
    deleteStepRequest,
} from "../../../redux/actions/careerPathStepActions";

import {
    fetchSeuilPassageRequest,
    createSeuilPassageRequest,
    updateSeuilPassageRequest,
} from "../../../redux/actions/seuilPassageActions";

import { getAllJobsRequest } from "../../../redux/actions/Jobactions";
import "./CareerPathManagement.css";

const findTransitionThreshold = (seuils, fromJobId, toJobId) =>
    (Array.isArray(seuils) ? seuils : []).find((s) =>
        Number(s.posteOrigineId) === Number(fromJobId) && Number(s.posteCibleId) === Number(toJobId)
    );

/* ═══════════════════════════════════════════════════
   MODAL réutilisable
═══════════════════════════════════════════════════ */
const Modal = ({ open, title, onClose, size = "lg", footer, children }) => {
    if (!open) return null;
    const sizeClass =
        size === "xl"  ? "cpModal__panel--xl"  :
            size === "xxl" ? "cpModal__panel--xxl" :
                size === "lg"  ? "cpModal__panel--lg"  : "";
    return (
        <div className="cpModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`cpModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="cpModal__header">
                    <div className="cpModal__title">{title}</div>
                    <button className="cpIconBtn" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="cpModal__body">{children}</div>
                {footer && <div className="cpModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   BADGE STATUT
═══════════════════════════════════════════════════ */
const StatusBadge = ({ status }) => {
    const map = {
        IN_PROGRESS: { label: "En cours",  cls: "cpBadge--blue"  },
        COMPLETED:   { label: "Terminé",   cls: "cpBadge--green" },
        ABANDONED:   { label: "Abandonné", cls: "cpBadge--grey"  },
    };
    const s = map[status] || { label: status, cls: "cpBadge--grey" };
    return <span className={`cpBadge ${s.cls}`}>{s.label}</span>;
};

const TransitionThresholdEditor = ({ fromStep, toStep, seuil, currentUser, dispatch }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        seuilGlobalMatch: seuil?.seuilGlobalMatch ?? 80,
        anneesExperienceRequises: seuil?.anneesExperienceRequises ?? 0,
        validationManagerRequise: seuil?.validationManagerRequise ?? true,
    });

    useEffect(() => {
        setForm({
            seuilGlobalMatch: seuil?.seuilGlobalMatch ?? 80,
            anneesExperienceRequises: seuil?.anneesExperienceRequises ?? 0,
            validationManagerRequise: seuil?.validationManagerRequise ?? true,
        });
    }, [seuil?.id, seuil?.seuilGlobalMatch, seuil?.anneesExperienceRequises, seuil?.validationManagerRequise]);

    const save = () => {
        if (!currentUser?.companyId || !fromStep?.job?.id || !toStep?.job?.id) {
            Swal.fire({ icon: "warning", text: "Impossible d'enregistrer ce seuil: entreprise ou postes indisponibles." });
            return;
        }

        const payload = {
            posteOrigineId: Number(fromStep.job.id),
            posteCibleId: Number(toStep.job.id),
            seuilGlobalMatch: Number(form.seuilGlobalMatch || 0),
            anneesExperienceRequises: Number(form.anneesExperienceRequises || 0),
            validationManagerRequise: Boolean(form.validationManagerRequise),
            companyId: currentUser.companyId,
        };

        if (seuil?.id) {
            dispatch(updateSeuilPassageRequest(seuil.id, payload));
        } else {
            dispatch(createSeuilPassageRequest(payload));
        }
        setOpen(false);
    };

    return (
        <div className="cpTransition">
            <div className="cpTransition__rail">
                <ArrowRight size={14} />
            </div>
            <div className="cpTransition__card">
                <div className="cpTransition__header">
                    <div>
                        <div className="cpTransition__title">Conditions de passage</div>
                        <div className="cpTransition__subtitle">
                            {fromStep?.job?.name || "Poste origine"} vers {toStep?.job?.name || "poste cible"}
                        </div>
                    </div>
                    <button className="cpBtn cpBtn--ghost" type="button" onClick={() => setOpen((v) => !v)}>
                        <Edit2 size={13} /> {seuil ? "Modifier" : "Configurer"}
                    </button>
                </div>

                <div className="cpTransition__badges">
                    {seuil ? (
                        <>
                            <span className="cpBadge cpBadge--blue"><Activity size={10} style={{ marginRight: 3 }} />{seuil.seuilGlobalMatch}% match</span>
                            <span className="cpBadge cpBadge--grey"><Clock size={10} style={{ marginRight: 3 }} />{seuil.anneesExperienceRequises ?? 0} an(s)</span>
                            <span className={`cpBadge ${seuil.validationManagerRequise ? "cpBadge--green" : "cpBadge--grey"}`}>
                                <UserCheck size={10} style={{ marginRight: 3 }} />{seuil.validationManagerRequise ? "Validation manager" : "Sans validation manager"}
                            </span>
                        </>
                    ) : (
                        <span className="cpTransition__empty">Aucun seuil configure pour cette transition.</span>
                    )}
                </div>

                {open && (
                    <div className="cpTransition__form">
                        <div className="cpFormGrid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                            <div className="cpField">
                                <label>Seuil global (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={form.seuilGlobalMatch}
                                    onChange={(e) => setForm((p) => ({ ...p, seuilGlobalMatch: e.target.value }))}
                                />
                            </div>
                            <div className="cpField">
                                <label>Experience requise (ans)</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.anneesExperienceRequises}
                                    onChange={(e) => setForm((p) => ({ ...p, anneesExperienceRequises: e.target.value }))}
                                />
                            </div>
                            <label className="cpTransition__check">
                                <input
                                    type="checkbox"
                                    checked={Boolean(form.validationManagerRequise)}
                                    onChange={(e) => setForm((p) => ({ ...p, validationManagerRequise: e.target.checked }))}
                                />
                                Validation manager requise
                            </label>
                        </div>
                        <div className="cpTransition__actions">
                            <button className="cpBtn cpBtn--ghost" type="button" onClick={() => setOpen(false)}>
                                <X size={13} /> Annuler
                            </button>
                            <button className="cpBtn cpBtn--primary" type="button" onClick={save}>
                                <Save size={13} /> Enregistrer le seuil
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   MODAL DÉTAILS D'UN PARCOURS
═══════════════════════════════════════════════════ */
const CareerPathDetailsModal = ({ open, careerPath, onClose, dispatch }) => {
    const { steps, loading: stepsLoading } = useSelector((s) => s.careerPathStep || {});
    const { employeeCareerPaths }          = useSelector((s) => s.employeeCareerPath || {});

    useEffect(() => {
        if (open && careerPath?.id) dispatch(getStepsByCareerPathRequest(careerPath.id));
    }, [open, careerPath?.id, dispatch]);

    const cpSteps   = useMemo(() => Array.isArray(steps) ? steps : [], [steps]);
    const employees = useMemo(() =>
            (Array.isArray(employeeCareerPaths) ? employeeCareerPaths : [])
                .filter((e) => e.careerPath?.id === careerPath?.id),
        [employeeCareerPaths, careerPath?.id]
    );

    if (!open || !careerPath) return null;

    return (
        <Modal
            open={open}
            title={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Eye size={16} style={{ color: "#dc3545" }} />
                    Détails — <span style={{ color: "#dc3545" }}>{careerPath.name}</span>
                </span>
            }
            onClose={onClose}
            size="xl"
            footer={<button className="cpBtn cpBtn--ghost" onClick={onClose} type="button">Fermer</button>}
        >
            <div className="cpFormContent">
                <div className="cpFormSection">
                    <h3 className="cpFormTitle">Informations générales</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px" }}>
                        <div className="cp-info-block">
                            <span className="cp-info-label">Nom</span>
                            <span className="cp-info-value" style={{ fontWeight: 700, color: "#2f4595" }}>{careerPath.name}</span>
                        </div>
                        <div className="cp-info-block">
                            <span className="cp-info-label">Département</span>
                            <span className="cp-info-value">{careerPath.department || "—"}</span>
                        </div>
                        <div className="cp-info-block">
                            <span className="cp-info-label">Statut</span>
                            {careerPath.isActive
                                ? <span className="cpBadge cpBadge--green"><CheckCircle size={11} style={{ marginRight: 4 }} />Actif</span>
                                : <span className="cpBadge cpBadge--grey"><XCircle size={11} style={{ marginRight: 4 }} />Inactif</span>
                            }
                        </div>
                        {careerPath.description && (
                            <div className="cp-info-block" style={{ gridColumn: "1 / -1" }}>
                                <span className="cp-info-label">Description</span>
                                <span className="cp-info-value">{careerPath.description}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="cpFormSection">
                    <h3 className="cpFormTitle"><Activity size={13} style={{ marginRight: 6 }} />Statistiques</h3>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <div className="cp-stat-card">
                            <span className="cp-stat-value">{cpSteps.length}</span>
                            <span className="cp-stat-label">étape{cpSteps.length !== 1 ? "s" : ""}</span>
                        </div>
                        <div className="cp-stat-card">
                            <span className="cp-stat-value">{employees.length}</span>
                            <span className="cp-stat-label">employé{employees.length !== 1 ? "s" : ""} assigné{employees.length !== 1 ? "s" : ""}</span>
                        </div>
                        <div className="cp-stat-card">
                            <span className="cp-stat-value">{cpSteps.reduce((s, e) => s + (e.minDurationMonths || 0), 0)}</span>
                            <span className="cp-stat-label">mois min. total</span>
                        </div>
                    </div>
                </div>

                <div className="cpFormSection">
                    <h3 className="cpFormTitle" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Étapes du parcours</span>
                        <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
                            {cpSteps.length} étape{cpSteps.length !== 1 ? "s" : ""}
                        </span>
                    </h3>
                    {stepsLoading ? (
                        <div className="cpLoading"><div className="cpSpinner" /><span>Chargement...</span></div>
                    ) : cpSteps.length === 0 ? (
                        <div className="cpEmptyCell">Aucune étape définie</div>
                    ) : (
                        <div className="cp-steps-timeline">
                            {cpSteps.map((step, idx) => (
                                <div className="cp-step-item" key={step.id}>
                                    <div className="cp-step-dot">{idx + 1}</div>
                                    {idx < cpSteps.length - 1 && <div className="cp-step-line" />}
                                    <div className="cp-step-content">
                                        <div className="cp-step-job">{step.job?.name || "—"}</div>
                                        <div className="cp-step-meta">
                                            {step.minDurationMonths && (
                                                <span className="cpBadge cpBadge--grey">
                                                    <Clock size={10} style={{ marginRight: 3 }} />
                                                    {step.minDurationMonths}–{step.maxDurationMonths || "?"} mois
                                                </span>
                                            )}
                                            {step.notes && <span className="cp-info-value">{step.notes}</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};


/* ═══════════════════════════════════════════════════
   MODAL ASSIGNATION EMPLOYÉ
═══════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════
   ONGLET — PARCOURS
═══════════════════════════════════════════════════ */
const CareerPathsTab = ({ careerPaths, loading, dispatch, onOpenDetails }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing,     setEditing]     = useState(null);
    const [form,        setForm]        = useState({ name: "", description: "", department: "", isActive: true });
    const [q,           setQ]           = useState("");
    const [filter,      setFilter]      = useState("all");
    const [expandedId,  setExpandedId]  = useState(null);

    const { steps } = useSelector((s) => s.careerPathStep || {});

    useEffect(() => { dispatch(getAllCareerPathsRequest()); }, [dispatch]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return (careerPaths || []).filter((cp) => {
            const mQ = !t || (cp.name || "").toLowerCase().includes(t) || (cp.department || "").toLowerCase().includes(t);
            const mF = filter === "all" ? true : filter === "active" ? !!cp.isActive : !cp.isActive;
            return mQ && mF;
        });
    }, [careerPaths, q, filter]);

    const openCreate = () => { setEditing(null); setForm({ name: "", description: "", department: "", isActive: true }); setIsModalOpen(true); };
    const openEdit   = (cp) => { setEditing(cp); setForm({ name: cp.name || "", description: cp.description || "", department: cp.department || "", isActive: cp.isActive !== undefined ? cp.isActive : true }); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); };
    const onChange   = (e) => { const { name, value, type, checked } = e.target; setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value })); };

    const submit = (e) => {
        e?.preventDefault();
        if (!form.name?.trim()) { Swal.fire({ icon: "warning", text: "Nom obligatoire." }); return; }
        const payload = { name: form.name.trim(), description: form.description?.trim() || "", department: form.department?.trim() || "", isActive: !!form.isActive };
        if (editing?.id) dispatch(updateCareerPathRequest(editing.id, payload));
        else             dispatch(createCareerPathRequest(payload));
        closeModal();
    };

    const confirmDelete = (cp) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer le parcours "${cp.name}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#dc3545",
        }).then((r) => { if (r.isConfirmed) dispatch(deleteCareerPathRequest(cp.id)); });
    };

    const toggleExpand = (id) => {
        setExpandedId((current) => {
            const next = current === id ? null : id;
            if (next) dispatch(getStepsByCareerPathRequest(next));
            return next;
        });
    };

    return (
        <>
            <div className="cpHeader">
                <div>
                    <h1>Gestion des parcours de carrière</h1>
                    <p>Définissez et gérez les trajectoires professionnelles de votre organisation</p>
                </div>
                <div className="cpHeaderActions">
                    <button className="cpBtn cpBtn--primary" onClick={openCreate} type="button">
                        <Plus size={16} /> Créer un parcours
                    </button>
                </div>
            </div>

            <div className="cpToolbar">
                <input type="text" className="cpSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, département..." />
                <div className="cpFilterTabs">
                    {[{ key: "all", label: "Tous" }, { key: "active", label: "Actifs" }, { key: "inactive", label: "Inactifs" }].map((f) => (
                        <button key={f.key} className={`cpFilterTab ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)} type="button">{f.label}</button>
                    ))}
                </div>
                <div className="cpMeta">{loading ? "..." : `${filtered.length} parcours`}</div>
            </div>

            <div className="cpWrap">
                <div className="cpCard">
                    <div className="cpBody">
                        <div className="cpTopRow">
                            <h2 className="cpTitle">Parcours ({loading ? "..." : filtered.length})</h2>
                        </div>
                        <div className="cpTableWrap">
                            <table className="cpTable">
                                <thead>
                                <tr>
                                    <th style={{ width: 36 }}></th>
                                    <th>Nom du parcours</th>
                                    <th>Département</th>
                                    <th>Étapes</th>
                                    <th>Statut</th>
                                    <th>Date création</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr><td colSpan={7}><div className="cpLoading"><div className="cpSpinner" /><span>Chargement...</span></div></td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={7} className="cpEmptyCell">Aucun parcours trouvé</td></tr>
                                ) : filtered.map((cp) => (
                                    <React.Fragment key={cp.id}>
                                        <tr>
                                            <td>
                                                <button className="cpIconBtn" onClick={() => toggleExpand(cp.id)} type="button" title="Voir les étapes">
                                                    {expandedId === cp.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                                </button>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 700, color: "#2f4595", fontSize: 12 }}>{cp.name}</div>
                                                {cp.description && (
                                                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {cp.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {cp.department
                                                    ? <span className="cpBadge cpBadge--blue">{cp.department}</span>
                                                    : <span className="cpMuted">—</span>
                                                }
                                            </td>
                                            <td>
                                                    <span className="cp-steps-count">
                                                        <Layers size={12} style={{ marginRight: 4 }} />
                                                        {cp.steps?.length ?? "—"} étape{(cp.steps?.length ?? 0) !== 1 ? "s" : ""}
                                                    </span>
                                            </td>
                                            <td>
                                                {cp.isActive
                                                    ? <span className="cpBadge cpBadge--green"><CheckCircle size={11} style={{ marginRight: 4 }} />Actif</span>
                                                    : <span className="cpBadge cpBadge--grey"><XCircle size={11} style={{ marginRight: 4 }} />Inactif</span>
                                                }
                                            </td>
                                            <td className="cpMuted" style={{ whiteSpace: "nowrap", fontSize: 11 }}>
                                                {cp.createdAt ? new Date(cp.createdAt).toLocaleDateString("fr-FR") : "—"}
                                            </td>
                                            <td className="is-right">
                                                <div className="cpRowActions">
                                                    <button className="cpIconBtn" title="Voir les détails" onClick={() => onOpenDetails(cp)} type="button"><Eye size={16} /></button>
                                                    <button className="cpIconBtn" title="Modifier" onClick={() => openEdit(cp)} type="button"><Edit2 size={16} /></button>
                                                    <button className="cpIconBtn cpIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(cp)} type="button"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedId === cp.id && (
                                            <tr className="cp-expand-row">
                                                <td colSpan={7}>
                                                    <div className="cp-expand-content">
                                                        {!Array.isArray(steps) || steps.length === 0 ? (
                                                            <span className="cpMuted" style={{ fontSize: 11 }}>
                                                                Aucune etape definie. Ajoutez les etapes depuis le constructeur de parcours.
                                                                </span>
                                                        ) : (
                                                            <div className="cp-mini-timeline">
                                                                {[...steps].sort((a, b) => a.stepOrder - b.stepOrder).map((s, idx, arr) => (
                                                                    <React.Fragment key={s.id}>
                                                                        <div className="cp-mini-step">
                                                                            <div className="cp-mini-dot">{s.stepOrder}</div>
                                                                            <div className="cp-mini-label">{s.job?.name || "—"}</div>
                                                                            {s.minDurationMonths && (
                                                                                <div className="cp-mini-duration">
                                                                                    <Clock size={9} /> {s.minDurationMonths}m
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {idx < arr.length - 1 && <ArrowRight size={14} className="cp-mini-arrow" />}
                                                                    </React.Fragment>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                open={isModalOpen}
                title={editing ? "Modifier le parcours" : "Créer un parcours"}
                onClose={closeModal}
                size="lg"
                footer={
                    <>
                        <button className="cpBtn cpBtn--ghost" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                        <button className="cpBtn cpBtn--primary" onClick={submit} disabled={loading} type="button">
                            <Save size={16} />{loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                        </button>
                    </>
                }
            >
                <form className="cpFormContent" onSubmit={submit}>
                    <div className="cpFormSection">
                        <h3 className="cpFormTitle">Informations du parcours</h3>
                        <div className="cpFormGrid">
                            <div className="cpField cpField--full">
                                <label>Nom du parcours *</label>
                                <input name="name" value={form.name} onChange={onChange} placeholder="Ex : Parcours Développeur Senior" maxLength={150} />
                            </div>
                            <div className="cpField">
                                <label>Département</label>
                                <input name="department" value={form.department} onChange={onChange} placeholder="Ex : Informatique" maxLength={100} />
                            </div>
                            <div className="cpField">
                                <label className="cpCheckLabel">
                                    <input type="checkbox" name="isActive" checked={form.isActive} onChange={onChange} />
                                    Parcours actif
                                </label>
                            </div>
                            <div className="cpField cpField--full">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={onChange} rows={3} placeholder="Description du parcours..." />
                            </div>
                        </div>
                    </div>
                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>
        </>
    );
};

/* ═══════════════════════════════════════════════════
   ONGLET — SUIVI EMPLOYÉS
═══════════════════════════════════════════════════ */
const StepBuilderCard = ({ step, index, total, jobs, onMove, onRemove, onUpdate, dragHandlers }) => {
    const [editOpen, setEditOpen] = useState(false);
    const [localMin,   setLocalMin]   = useState(step.minDurationMonths ?? "");
    const [localMax,   setLocalMax]   = useState(step.maxDurationMonths ?? "");
    const [localNotes, setLocalNotes] = useState(step.notes ?? "");

    /* sync si le step change de l'extérieur */
    useEffect(() => {
        setLocalMin(step.minDurationMonths ?? "");
        setLocalMax(step.maxDurationMonths ?? "");
        setLocalNotes(step.notes ?? "");
    }, [step.id]);

    const commitEdit = () => {
        onUpdate(step.id, {
            minDurationMonths: localMin !== "" ? Number(localMin) : null,
            maxDurationMonths: localMax !== "" ? Number(localMax) : null,
            notes: localNotes || null,
        });
        setEditOpen(false);
    };

    return (
        <div
            className={`cpBuilder__stepCard ${editOpen ? "cpBuilder__stepCard--editing" : ""}`}
            draggable
            onDragStart={dragHandlers.onDragStart}
            onDragEnd={dragHandlers.onDragEnd}
            onDragOver={dragHandlers.onDragOver}
            onDrop={dragHandlers.onDrop}
            data-step-id={step.id}
        >
            {/* Connecteur vertical */}
            {index < total - 1 && <div className="cpBuilder__connector" />}

            {/* Numéro */}
            <div className="cpBuilder__dot">{index + 1}</div>

            {/* Corps */}
            <div className="cpBuilder__cardBody">
                {/* Actions en haut à droite */}
                <div className="cpBuilder__cardActions">
                    <button
                        className="cpIconBtn"
                        title="Glisser pour réordonner"
                        style={{ cursor: "grab", color: "#9ca3af" }}
                        type="button"
                    >
                        <GripVertical size={14} />
                    </button>
                    <button
                        className="cpIconBtn"
                        title="Monter"
                        onClick={() => onMove(step.id, -1)}
                        disabled={index === 0}
                        type="button"
                    >
                        <ArrowUp size={14} />
                    </button>
                    <button
                        className="cpIconBtn"
                        title="Descendre"
                        onClick={() => onMove(step.id, 1)}
                        disabled={index === total - 1}
                        type="button"
                    >
                        <ArrowDown size={14} />
                    </button>
                    <button
                        className={`cpIconBtn ${editOpen ? "cpIconBtn--active" : ""}`}
                        title="Modifier"
                        onClick={() => setEditOpen((v) => !v)}
                        type="button"
                    >
                        <Edit2 size={14} />
                    </button>
                    <button
                        className="cpIconBtn cpIconBtn--danger"
                        title="Supprimer"
                        onClick={() => onRemove(step.id)}
                        type="button"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>

                {/* Infos principales */}
                <div className="cpBuilder__jobName">
                    <Briefcase size={13} style={{ color: "#9ca3af", marginRight: 6, flexShrink: 0 }} />
                    {step.job?.name || "—"}
                </div>
                <div className="cpBuilder__jobCode">{step.job?.code}</div>

                {/* Méta (badges) */}
                <div className="cpBuilder__meta">
                    {(step.minDurationMonths || step.maxDurationMonths) && (
                        <span className="cpBadge cpBadge--grey">
                            <Clock size={10} style={{ marginRight: 3 }} />
                            {step.minDurationMonths ?? "?"}–{step.maxDurationMonths ?? "?"} mois
                        </span>
                    )}
                    {step.notes && (
                        <span className="cpBadge cpBadge--grey" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {step.notes}
                        </span>
                    )}
                </div>

                {/* Panneau d'édition inline */}
                {editOpen && (
                    <div className="cpBuilder__editPanel">
                        <div className="cpFormGrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <div className="cpField">
                                <label>Durée min. (mois)</label>
                                <input
                                    type="number"
                                    value={localMin}
                                    onChange={(e) => setLocalMin(e.target.value)}
                                    min={0}
                                    placeholder="Ex : 6"
                                />
                            </div>
                            <div className="cpField">
                                <label>Durée max. (mois)</label>
                                <input
                                    type="number"
                                    value={localMax}
                                    onChange={(e) => setLocalMax(e.target.value)}
                                    min={0}
                                    placeholder="Ex : 12"
                                />
                            </div>
                            <div className="cpField cpField--full">
                                <label>Notes</label>
                                <input
                                    type="text"
                                    value={localNotes}
                                    onChange={(e) => setLocalNotes(e.target.value)}
                                    placeholder="Remarques sur cette étape..."
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                            <button className="cpBtn cpBtn--ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setEditOpen(false)} type="button">
                                <X size={13} /> Annuler
                            </button>
                            <button className="cpBtn cpBtn--primary" style={{ padding: "6px 12px", fontSize: 11 }} onClick={commitEdit} type="button">
                                <Save size={13} /> Valider
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   ONGLET — CONSTRUCTEUR DE PARCOURS
═══════════════════════════════════════════════════ */
const CareerPathBuilderTab = ({ careerPaths, dispatch }) => {
    const { steps: reduxSteps, loading: stepsLoading } = useSelector((s) => s.careerPathStep || {});
    const { jobs, loading: jobsLoading }               = useSelector((s) => s.jobs || {});
    const { list: seuils }                             = useSelector((s) => s.seuilPassage || { list: [] });
    const currentUser                                  = useSelector((s) => s.auth?.user);

    /* Parcours sélectionné */
    const [selectedId, setSelectedId]   = useState(null);
    const [q,          setQ]            = useState("");

    /* Étapes locales (copie pour édition avant save) */
    const [localSteps,    setLocalSteps]    = useState([]);
    const [originalSteps, setOriginalSteps] = useState([]);
    const [unsaved,       setUnsaved]       = useState(false);

    /* Formulaire ajout */
    const [addForm, setAddForm] = useState({ jobId: "", minDurationMonths: "", maxDurationMonths: "", notes: "" });

    /* Drag and drop */
    const dragSrcId = React.useRef(null);

    /* ── Chargement initial ── */
    useEffect(() => {
        dispatch(getAllCareerPathsRequest());
        dispatch(getAllJobsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser?.companyId) {
            dispatch(fetchSeuilPassageRequest(currentUser.companyId));
        }
    }, [dispatch, currentUser?.companyId]);

    /* ── Quand on sélectionne un parcours → charger ses étapes ── */
    const handleSelectCP = useCallback((cp) => {
        setSelectedId(cp.id);
        setUnsaved(false);
        dispatch(getStepsByCareerPathRequest(cp.id));
    }, [dispatch]);

    /* ── Sync étapes Redux → local quand on change de parcours ── */
    useEffect(() => {
        if (selectedId === null) return;
        const sorted = Array.isArray(reduxSteps)
            ? [...reduxSteps].sort((a, b) => a.stepOrder - b.stepOrder)
            : [];
        setLocalSteps(sorted.map((s, i) => ({ ...s, stepOrder: i + 1 })));
        setOriginalSteps(sorted.map((s, i) => ({ ...s, stepOrder: i + 1 })));
        setUnsaved(false);
    }, [reduxSteps, selectedId]);

    const selectedCP  = useMemo(() => (careerPaths || []).find((cp) => cp.id === selectedId), [careerPaths, selectedId]);
    const jobsList    = useMemo(() => Array.isArray(jobs) ? jobs : [], [jobs]);

    const filteredCPs = useMemo(() => {
        const t = q.trim().toLowerCase();
        return (careerPaths || []).filter((cp) =>
            !t || (cp.name || "").toLowerCase().includes(t) || (cp.department || "").toLowerCase().includes(t)
        );
    }, [careerPaths, q]);

    const totalMinMonths = useMemo(() => localSteps.reduce((s, e) => s + (e.minDurationMonths || 0), 0), [localSteps]);

    /* ── Helpers étapes locales ── */
    const markUnsaved = () => setUnsaved(true);

    const reorder = (arr) => arr.map((s, i) => ({ ...s, stepOrder: i + 1 }));

    const handleAddStep = () => {
        if (!addForm.jobId) { Swal.fire({ icon: "warning", text: "Veuillez choisir un poste." }); return; }
        const job = jobsList.find((j) => j.id === Number(addForm.jobId));
        if (!job) return;
        const newStep = {
            id: `temp_${Date.now()}`,
            stepOrder: localSteps.length + 1,
            job,
            minDurationMonths: addForm.minDurationMonths ? Number(addForm.minDurationMonths) : null,
            maxDurationMonths: addForm.maxDurationMonths ? Number(addForm.maxDurationMonths) : null,
            notes: addForm.notes || null,
        };
        setLocalSteps((prev) => reorder([...prev, newStep]));
        setAddForm({ jobId: "", minDurationMonths: "", maxDurationMonths: "", notes: "" });
        markUnsaved();
    };

    const handleRemoveStep = (id) => {
        setLocalSteps((prev) => reorder(prev.filter((s) => s.id !== id)));
        markUnsaved();
    };

    const handleMoveStep = (id, dir) => {
        setLocalSteps((prev) => {
            const arr = [...prev];
            const idx = arr.findIndex((s) => s.id === id);
            const target = idx + dir;
            if (target < 0 || target >= arr.length) return prev;
            [arr[idx], arr[target]] = [arr[target], arr[idx]];
            return reorder(arr);
        });
        markUnsaved();
    };

    const handleUpdateStep = (id, updates) => {
        setLocalSteps((prev) => prev.map((s) => s.id === id ? { ...s, ...updates } : s));
        markUnsaved();
    };

    /* ── Drag & Drop ── */
    const dragHandlersFor = (stepId) => ({
        onDragStart: (e) => { dragSrcId.current = stepId; e.currentTarget.style.opacity = "0.5"; },
        onDragEnd:   (e) => { e.currentTarget.style.opacity = "1"; },
        onDragOver:  (e) => { e.preventDefault(); },
        onDrop: (e) => {
            e.preventDefault();
            if (!dragSrcId.current || dragSrcId.current === stepId) return;
            setLocalSteps((prev) => {
                const arr   = [...prev];
                const fromI = arr.findIndex((s) => s.id === dragSrcId.current);
                const toI   = arr.findIndex((s) => s.id === stepId);
                if (fromI < 0 || toI < 0) return prev;
                const [moved] = arr.splice(fromI, 1);
                arr.splice(toI, 0, moved);
                return reorder(arr);
            });
            dragSrcId.current = null;
            markUnsaved();
        },
    });

    /* ── Sauvegarde ── */
    const handleSave = async () => {
        if (!selectedId) return;

        /* Étapes à supprimer (existaient avant, plus dans local) */
        const origIds  = originalSteps.filter((s) => !String(s.id).startsWith("temp_")).map((s) => s.id);
        const localIds = localSteps.filter((s) => !String(s.id).startsWith("temp_")).map((s) => s.id);
        const toDelete = origIds.filter((id) => !localIds.includes(id));
        toDelete.forEach((id) => dispatch(deleteStepRequest(selectedId, id)));

        /* Étapes à créer ou mettre à jour */
        localSteps.forEach((step) => {
            const payload = {
                jobId:             step.job?.id,
                stepOrder:         step.stepOrder,
                minDurationMonths: step.minDurationMonths,
                maxDurationMonths: step.maxDurationMonths,
                notes:             step.notes,
            };
            if (String(step.id).startsWith("temp_")) {
                dispatch(addStepRequest(selectedId, payload));
            } else {
                const orig = originalSteps.find((s) => s.id === step.id);
                const changed = !orig
                    || orig.stepOrder         !== step.stepOrder
                    || orig.minDurationMonths !== step.minDurationMonths
                    || orig.maxDurationMonths !== step.maxDurationMonths
                    || orig.notes             !== step.notes;
                if (changed) dispatch(updateStepRequest(selectedId, step.id, payload));
            }
        });

        setUnsaved(false);
        Swal.fire({ icon: "success", title: "Enregistré !", text: "Le parcours a été mis à jour.", timer: 2000, showConfirmButton: false });
    };

    const handleDiscard = () => {
        setLocalSteps(originalSteps.map((s) => ({ ...s })));
        setUnsaved(false);
    };

    /* ── Render ── */
    return (
        <div className="cpBuilder__page">

            {/* ── Colonne gauche : liste parcours ── */}
            <div className="cpBuilder__sidebar">
                <div className="cpBuilder__sidebarHeader">
                    <div style={{ fontWeight: 800, fontSize: 12, color: "#2f4595", textTransform: "uppercase", letterSpacing: ".5px" }}>
                        Parcours de carrière
                    </div>
                    <input
                        type="text"
                        className="cpSearch"
                        style={{ marginTop: 8 }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filtrer..."
                    />
                </div>
                <div className="cpBuilder__cpList">
                    {filteredCPs.length === 0 && (
                        <div className="cpEmptyCell">Aucun parcours</div>
                    )}
                    {filteredCPs.map((cp) => (
                        <button
                            key={cp.id}
                            type="button"
                            className={`cpBuilder__cpItem ${selectedId === cp.id ? "cpBuilder__cpItem--active" : ""}`}
                            onClick={() => handleSelectCP(cp)}
                        >
                            <div className="cpBuilder__cpItemName">{cp.name}</div>
                            <div style={{ display: "flex", gap: 5, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                                {cp.department && (
                                    <span className="cpBadge cpBadge--blue" style={{ fontSize: 10, padding: "2px 6px" }}>{cp.department}</span>
                                )}
                                <span className={`cpBadge ${cp.isActive ? "cpBadge--green" : "cpBadge--grey"}`} style={{ fontSize: 10, padding: "2px 6px" }}>
                                    {cp.isActive ? "Actif" : "Inactif"}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Colonne droite : builder ── */}
            <div className="cpBuilder__main">
                {!selectedCP ? (
                    <div className="cpBuilder__placeholder">
                        <GitBranch size={40} style={{ color: "#d1d5db", marginBottom: 12 }} />
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>Sélectionnez un parcours</div>
                        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                            Choisissez un parcours dans la liste pour construire ses étapes
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Info bar */}
                        <div className="cpBuilder__infoBar">
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 14, color: "#2f4595" }}>{selectedCP.name}</div>
                                {selectedCP.description && (
                                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{selectedCP.description}</div>
                                )}
                            </div>
                            <span className="cpBadge cpBadge--grey" style={{ gap: 5 }}>
                                <Layers size={11} /> {localSteps.length} étape{localSteps.length !== 1 ? "s" : ""}
                            </span>
                            <span className="cpBadge cpBadge--grey" style={{ gap: 5 }}>
                                <Clock size={11} /> {totalMinMonths} mois min.
                            </span>
                            <span className={`cpBadge ${selectedCP.isActive ? "cpBadge--green" : "cpBadge--grey"}`}>
                                {selectedCP.isActive ? <><CheckCircle size={11} style={{ marginRight: 3 }} />Actif</> : <><XCircle size={11} style={{ marginRight: 3 }} />Inactif</>}
                            </span>
                        </div>

                        {/* Formulaire ajout */}
                        <div className="cpBuilder__addForm">
                            <div style={{ fontWeight: 800, fontSize: 11, color: "#2f4595", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10 }}>
                                Ajouter une étape
                            </div>
                            <div className="cpBuilder__addGrid">
                                <div className="cpField" style={{ gridColumn: "1 / 3" }}>
                                    <label>Poste *</label>
                                    <select
                                        value={addForm.jobId}
                                        onChange={(e) => setAddForm((p) => ({ ...p, jobId: e.target.value }))}
                                        disabled={jobsLoading}
                                    >
                                        <option value="">-- choisir un poste --</option>
                                        {jobsList.map((j) => (
                                            <option key={j.id} value={j.id}>{j.name} ({j.code})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="cpField">
                                    <label>Durée min. (mois)</label>
                                    <input
                                        type="number"
                                        value={addForm.minDurationMonths}
                                        onChange={(e) => setAddForm((p) => ({ ...p, minDurationMonths: e.target.value }))}
                                        min={0}
                                        placeholder="Ex : 6"
                                    />
                                </div>
                                <div className="cpField">
                                    <label>Durée max. (mois)</label>
                                    <input
                                        type="number"
                                        value={addForm.maxDurationMonths}
                                        onChange={(e) => setAddForm((p) => ({ ...p, maxDurationMonths: e.target.value }))}
                                        min={0}
                                        placeholder="Ex : 12"
                                    />
                                </div>
                                <div className="cpField" style={{ gridColumn: "1 / 4" }}>
                                    <label>Notes (optionnel)</label>
                                    <input
                                        type="text"
                                        value={addForm.notes}
                                        onChange={(e) => setAddForm((p) => ({ ...p, notes: e.target.value }))}
                                        placeholder="Remarques sur cette étape..."
                                    />
                                </div>
                                <div className="cpField" style={{ gridColumn: "4 / 5", alignSelf: "flex-end" }}>
                                    <button
                                        className="cpBtn cpBtn--primary"
                                        style={{ width: "100%" }}
                                        onClick={handleAddStep}
                                        type="button"
                                    >
                                        <Plus size={14} /> Ajouter
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="cpBuilder__timelineWrap">
                            {stepsLoading ? (
                                <div className="cpLoading"><div className="cpSpinner" /><span>Chargement des étapes...</span></div>
                            ) : localSteps.length === 0 ? (
                                <div className="cpBuilder__emptyTimeline">
                                    <Layers size={32} style={{ color: "#d1d5db", marginBottom: 10 }} />
                                    <div style={{ fontWeight: 700, color: "#374151" }}>Aucune étape définie</div>
                                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                                        Utilisez le formulaire ci-dessus pour ajouter des postes dans l'ordre
                                    </div>
                                </div>
                            ) : (
                                <div className="cp-steps-timeline" style={{ paddingBottom: 24 }}>
                                    {localSteps.map((step, idx) => (
                                        <React.Fragment key={step.id}>
                                            <StepBuilderCard
                                                step={step}
                                                index={idx}
                                                total={localSteps.length}
                                                jobs={jobsList}
                                                onMove={handleMoveStep}
                                                onRemove={handleRemoveStep}
                                                onUpdate={handleUpdateStep}
                                                dragHandlers={dragHandlersFor(step.id)}
                                            />
                                            {localSteps[idx + 1] && !String(step.id).startsWith("temp_") && !String(localSteps[idx + 1].id).startsWith("temp_") && (
                                                <TransitionThresholdEditor
                                                    fromStep={step}
                                                    toStep={localSteps[idx + 1]}
                                                    seuil={findTransitionThreshold(seuils, step.job?.id, localSteps[idx + 1].job?.id)}
                                                    currentUser={currentUser}
                                                    dispatch={dispatch}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Barre de sauvegarde */}
                        {unsaved && (
                            <div className="cpBuilder__saveBar">
                                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc3545", display: "inline-block" }} />
                                    Modifications non enregistrées
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button className="cpBtn cpBtn--ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={handleDiscard} type="button">
                                        <X size={13} /> Annuler
                                    </button>
                                    <button className="cpBtn cpBtn--primary" style={{ padding: "6px 12px", fontSize: 11 }} onClick={handleSave} type="button">
                                        <Save size={13} /> Enregistrer
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
═══════════════════════════════════════════════════ */
const CareerPathManagement = ({ careerPaths, loading, dispatch }) => {
    const [activeTab,   setActiveTab]   = useState("paths");
    const [detailsJob,  setDetailsJob]  = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const openDetailsModal  = (cp) => { setDetailsJob(cp); setDetailsOpen(true); };
    const closeDetailsModal = ()   => { setDetailsOpen(false); setDetailsJob(null); };

    return (
        <div className="cpPage">
            {/* ── Tab Bar ── */}
            <div className="cpTabBar">
                <button
                    className={`cpTab ${activeTab === "paths" ? "cpTab--active" : ""}`}
                    onClick={() => setActiveTab("paths")}
                    type="button"
                >
                    <GitBranch size={14} style={{ marginRight: 6 }} />
                    Parcours de carrière
                </button>
                <button
                    className={`cpTab ${activeTab === "builder" ? "cpTab--active" : ""}`}
                    onClick={() => setActiveTab("builder")}
                    type="button"
                >
                    <Hammer size={14} style={{ marginRight: 6 }} />
                    Constructeur de parcours
                </button>
            </div>

            {/* ── Onglets ── */}
            {activeTab === "paths" && (
                <CareerPathsTab
                    careerPaths={careerPaths}
                    loading={loading}
                    dispatch={dispatch}
                    onOpenDetails={openDetailsModal}
                />
            )}

            {activeTab === "builder" && (
                <CareerPathBuilderTab
                    careerPaths={careerPaths}
                    dispatch={dispatch}
                />
            )}

            <CareerPathDetailsModal
                open={detailsOpen}
                careerPath={detailsJob}
                onClose={closeDetailsModal}
                dispatch={dispatch}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    careerPaths:         state.careerPath?.careerPaths              || [],
    loading:             state.careerPath?.loading                  || false,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(CareerPathManagement);
